import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Users, ArrowRight, Check, X,
  DollarSign, TrendingUp, Layers, Zap,
  UserCheck, Settings, Handshake, Wrench,
  Database, Cloud, Brain, GitMerge,
  Search, PenTool, Play, RefreshCw
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function TalentPodsPage() {
  const heroBullets = [
    { icon: DollarSign, text: 'Fixed monthly pods, no placement fees or hidden charges.' },
    { icon: TrendingUp, text: 'Scale up or down across data, cloud and AI skills.' },
    { icon: Layers, text: 'Get full squads for less than the cost of one senior hire.' },
    { icon: Zap, text: 'Activate pods in days, not months.' }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: 'Predictable costs',
      description: 'Fixed monthly fees with no surprises or hidden charges.'
    },
    {
      icon: TrendingUp,
      title: 'Elastic capacity',
      description: 'Scale your team up or down as your roadmap evolves.'
    },
    {
      icon: Layers,
      title: 'Full-stack squads',
      description: 'Complete teams with all the skills you need.'
    },
    {
      icon: Zap,
      title: 'Faster delivery',
      description: 'Skip the hiring cycle and start delivering immediately.'
    },
    {
      icon: UserCheck,
      title: 'Lower hiring risk',
      description: 'No long-term commitments or severance concerns.'
    }
  ];

  const podIncludes = [
    {
      icon: UserCheck,
      title: 'Curated engineering team',
      description: 'Pre-vetted engineers matched to your stack and domain.'
    },
    {
      icon: Settings,
      title: 'Managed operations',
      description: 'We handle HR, contracts, performance and replacement cover.'
    },
    {
      icon: Handshake,
      title: 'Integrated ways of working',
      description: 'Pods plug into your ceremonies, tools and processes.'
    },
    {
      icon: Wrench,
      title: 'Tooling & accelerators',
      description: 'Access to our frameworks, templates and best practices.'
    }
  ];

  const podModels = [
    {
      icon: Database,
      title: 'Data Pod',
      description: 'Pipelines, warehouses, dashboards, governance.',
      team: '2–4 data engineers + analytics lead',
      scenarios: 'Data platform builds, BI modernization, ongoing data ops.',
      minEngagement: '3 months minimum'
    },
    {
      icon: Cloud,
      title: 'Cloud Pod',
      description: 'Azure foundation, IaC, DevOps, modernization.',
      team: '2–4 cloud/DevOps engineers + architect',
      scenarios: 'Cloud migration, landing zone, platform engineering.',
      minEngagement: '3 months minimum'
    },
    {
      icon: Brain,
      title: 'AI Pod',
      description: 'LLMs, AI on BI, copilots, automation.',
      team: '2–3 AI/ML engineers + AI lead',
      scenarios: 'AI pilots, copilot development, production ML.',
      minEngagement: '3 months minimum'
    },
    {
      icon: GitMerge,
      title: 'Hybrid Pod',
      description: 'Blended team across data, cloud and AI.',
      team: 'Mix of skills based on your needs',
      scenarios: 'Multi-domain initiatives, full platform builds.',
      minEngagement: '3 months minimum'
    }
  ];

  const processSteps = [
    { icon: Search, title: 'Scope', description: 'Map roadmap, stack, gaps.' },
    { icon: PenTool, title: 'Design the pod', description: 'Size, skills, seniority, model.' },
    { icon: Play, title: 'Deploy', description: 'You meet the pod, approve, we onboard.' },
    { icon: RefreshCw, title: 'Run & optimize', description: 'Review performance, adjust pod composition.' }
  ];

  const plans = [
    {
      name: 'Starter Pod',
      description: 'For MVPs and small initiatives.',
      price: 'USD 3,299',
      priceNote: 'per month for 1 seat',
      team: '1 engineer',
      useCases: 'Early-stage projects, proofs of concept.',
      features: [
        { name: 'Dedicated engineer', included: true },
        { name: 'Agreed sprint / delivery model', included: true },
        { name: 'Replacement cover', included: true },
        { name: 'Basic delivery reporting', included: true },
        { name: 'Pod lead', included: false },
        { name: 'Architect oversight', included: false }
      ],
      popular: false
    },
    {
      name: 'Growth Pod',
      description: 'For scaling products and transformation programs.',
      price: 'USD 5,999',
      priceNote: 'per month for 2 seats',
      team: '2 engineers',
      useCases: 'Product squads, data platform build, ongoing cloud work.',
      features: [
        { name: 'Dedicated pod lead', included: true },
        { name: 'Agreed sprint / delivery model', included: true },
        { name: 'Replacement cover', included: true },
        { name: 'Delivery reporting', included: true },
        { name: 'Cross-skill coverage', included: true },
        { name: 'Architect oversight (optional)', included: true }
      ],
      popular: true
    },
    {
      name: 'Scale Pod',
      description: 'For large, multi-stream initiatives.',
      price: 'USD 9,999',
      priceNote: 'per month for 4 seats',
      team: '4 engineers',
      useCases: 'Enterprise-wide modernization, multiple streams.',
      features: [
        { name: 'Senior pod lead', included: true },
        { name: 'Agreed sprint / delivery model', included: true },
        { name: 'Priority replacement cover', included: true },
        { name: 'Advanced reporting & metrics', included: true },
        { name: 'Full-stack skill coverage', included: true },
        { name: 'Architect oversight included', included: true }
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'Can I change the composition of a pod mid-engagement?',
      answer: 'Yes, pods are designed to be flexible. You can adjust skills, seniority mix and team size with 2-4 weeks notice depending on the change.'
    },
    {
      question: 'How quickly can a pod start?',
      answer: 'Typically 1-2 weeks for Starter and Growth pods, and we prioritize Scale pod deployments.'
    },
    {
      question: 'Do pods work in my time zone?',
      answer: 'Our pods are based in India and Singapore but we offer flexible overlap hours. For Scale pods, we can arrange global coverage.'
    },
    {
      question: 'Can I use pods for short-term projects?',
      answer: 'Yes, though we recommend a minimum 3-month engagement to allow for proper onboarding and delivery momentum.'
    },
    {
      question: 'What if I need non-engineering roles?',
      answer: 'PM, QA and other roles can be added as optional add-ons to any pod.'
    }
  ];

  return (
    <div data-testid="talent-pods-page">
      <Helmet>
        <title>Engineering Talent - Cynergy Services</title>
        <meta name="description" content="Scale your data, cloud and AI teams on-demand with dedicated engineering pods. Fixed monthly pricing, no placement fees." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="pods-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm font-medium uppercase tracking-widest text-primary">Service</p>
              </div>
              <h1 className="heading-1 text-foreground mb-6">Engineering Talent as a Service</h1>
              <p className="body-lg mb-8">
                Scale your data, cloud and AI teams on-demand with fixed, predictable monthly pods 
                instead of unpredictable hiring costs.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {heroBullets.map((bullet, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`pods-bullet-${index}`}>
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <bullet.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground text-sm">{bullet.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="pods-cta-primary">
                    Design your pod
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="pods-cta-secondary">
                    View pricing
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/tn0tewb2_Engineering%20Talent.png"
                alt="Engineering team collaboration"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white" data-testid="pods-benefits">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">The Model</p>
            <h2 className="heading-2 text-foreground mb-4">Why Engineering Talent as a Service?</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Traditional hiring is slow, expensive and rigid. Our pods give you elastic engineering 
              capacity aligned to your roadmap, not recruitment cycles.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-secondary/30 border-0 text-center" data-testid={`pods-benefit-${index}`}>
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3 mx-auto">
                      <benefit.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pod Models */}
      <section className="section-padding bg-secondary/50" data-testid="pods-models">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">Pod Types</p>
            <h2 className="heading-2 text-foreground">Pod models</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {podModels.map((pod, index) => (
              <motion.div
                key={pod.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-border/60 card-hover" data-testid={`pod-model-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                        <pod.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="heading-3 text-foreground">{pod.title}</h3>
                    </div>
                    <p className="text-muted-foreground mb-4">{pod.description}</p>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium text-primary">Team:</span> <span className="text-muted-foreground">{pod.team}</span></p>
                      <p><span className="font-medium text-primary">Scenarios:</span> <span className="text-muted-foreground">{pod.scenarios}</span></p>
                      <p className="text-xs text-muted-foreground pt-2 border-t">{pod.minEngagement}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-foreground text-white" data-testid="pods-process">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">Process</p>
            <h2 className="heading-2">How it works</h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`pods-process-step-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-3 mx-auto">
                  <step.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-xs font-medium text-primary mb-1 block">Step {index + 1}</span>
                <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{step.title}</h3>
                <p className="text-white/70 text-xs">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white/10 rounded-sm p-6 max-w-2xl mx-auto">
            <h4 className="font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Who manages what?</h4>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-primary mb-2">You</p>
                <p className="text-sm text-white/70">Product priorities, backlog, acceptance.</p>
              </div>
              <div>
                <p className="text-sm font-medium text-primary mb-2">Us</p>
                <p className="text-sm text-white/70">Talent, HR, contracts, backup, performance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-padding bg-white" data-testid="pods-pricing">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">Pricing</p>
            <h2 className="heading-2 text-foreground mb-4">Simple, transparent pod pricing</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Fixed monthly fees, no placement fees or hidden charges. Scale month-to-month.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <Card 
                  className={`h-full ${plan.popular ? 'border-2 border-primary shadow-lg' : 'border border-border/60'}`}
                  data-testid={`pricing-plan-${index}`}
                >
                  <CardContent className="p-6">
                    <h3 className="heading-3 text-foreground mb-2">{plan.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {plan.price}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{plan.priceNote}</p>
                    </div>
                    
                    <div className="mb-4 pb-4 border-b border-border text-sm">
                      <p><span className="font-medium">Team:</span> {plan.team}</p>
                      <p className="text-muted-foreground mt-1">{plan.useCases}</p>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-primary" />
                          ) : (
                            <X className="w-4 h-4 text-muted-foreground/50" />
                          )}
                          <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50'}>
                            {feature.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link to="/contact">
                      <Button 
                        className={`w-full ${plan.popular ? 'btn-primary' : ''}`}
                        variant={plan.popular ? 'default' : 'outline'}
                        data-testid={`pricing-plan-cta-${index}`}
                      >
                        Get started
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            All plans are month-to-month with minimum 3-month recommendation. Custom pods and enterprise arrangements available on request.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-secondary/50" data-testid="pods-faq">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">FAQ</p>
            <h2 className="heading-2 text-foreground">Frequently asked questions</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border border-border rounded-sm px-4"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-3 text-sm">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-3 text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary" data-testid="pods-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">Design your engineering pod</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Tell us about your roadmap and we'll design a pod that fits your needs.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90"
                data-testid="pods-final-cta"
              >
                Get started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
